import { useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_MARGIN = 12;
const DRAG_THRESHOLD_PX = 6;

function readStoredPosition(storageKey) {
  if (!storageKey || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const x = Number(parsed?.x);
    const y = Number(parsed?.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    return { x, y };
  } catch {
    return null;
  }
}

function writeStoredPosition(storageKey, position) {
  if (!storageKey || !position || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      storageKey,
      JSON.stringify({ x: Math.round(position.x), y: Math.round(position.y) })
    );
  } catch {}
}

function viewportSize() {
  if (typeof window === "undefined") {
    return { width: 0, height: 0 };
  }
  return {
    width: Number(window.visualViewport?.width || window.innerWidth || 0),
    height: Number(window.visualViewport?.height || window.innerHeight || 0),
  };
}

export default function useDraggableFloatingPill(
  storageKey,
  { margin = DEFAULT_MARGIN } = {}
) {
  const elementRef = useRef(null);
  const dragRef = useRef(null);
  const suppressClickRef = useRef(false);
  const [position, setPosition] = useState(() => readStoredPosition(storageKey));
  const [dragging, setDragging] = useState(false);

  const clampPosition = useCallback(
    (nextX, nextY) => {
      const node = elementRef.current;
      const { width: viewportWidth, height: viewportHeight } = viewportSize();
      const width = Number(node?.offsetWidth || 0);
      const height = Number(node?.offsetHeight || 0);

      const maxX = Math.max(margin, viewportWidth - width - margin);
      const maxY = Math.max(margin, viewportHeight - height - margin);

      return {
        x: Math.min(maxX, Math.max(margin, Number(nextX) || margin)),
        y: Math.min(maxY, Math.max(margin, Number(nextY) || margin)),
      };
    },
    [margin]
  );

  useEffect(() => {
    setPosition(readStoredPosition(storageKey));
  }, [storageKey]);

  const hasCustomPosition = position !== null;

  useEffect(() => {
    if (!hasCustomPosition) return undefined;

    const keepInsideViewport = () => {
      setPosition((current) => {
        if (!current) return current;
        const next = clampPosition(current.x, current.y);
        if (next.x === current.x && next.y === current.y) return current;
        writeStoredPosition(storageKey, next);
        return next;
      });
    };

    keepInsideViewport();
    window.addEventListener("resize", keepInsideViewport);
    window.visualViewport?.addEventListener?.("resize", keepInsideViewport);
    window.visualViewport?.addEventListener?.("scroll", keepInsideViewport);

    return () => {
      window.removeEventListener("resize", keepInsideViewport);
      window.visualViewport?.removeEventListener?.("resize", keepInsideViewport);
      window.visualViewport?.removeEventListener?.("scroll", keepInsideViewport);
    };
  }, [clampPosition, hasCustomPosition, storageKey]);

  const finishDrag = useCallback(
    (event) => {
      const active = dragRef.current;
      if (!active || active.pointerId !== event.pointerId) return;

      try {
        elementRef.current?.releasePointerCapture?.(event.pointerId);
      } catch {}

      dragRef.current = null;
      setDragging(false);

      if (active.moved) {
        suppressClickRef.current = true;
        window.setTimeout(() => {
          suppressClickRef.current = false;
        }, 450);
        setPosition((current) => {
          if (current) writeStoredPosition(storageKey, current);
          return current;
        });
      }
    },
    [storageKey]
  );

  const pointerHandlers = {
    onPointerDown: (event) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      const node = elementRef.current;
      if (!node) return;

      const rect = node.getBoundingClientRect();
      dragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: rect.left,
        originY: rect.top,
        moved: false,
      };

      try {
        node.setPointerCapture?.(event.pointerId);
      } catch {}
    },
    onPointerMove: (event) => {
      const active = dragRef.current;
      if (!active || active.pointerId !== event.pointerId) return;

      const dx = event.clientX - active.startX;
      const dy = event.clientY - active.startY;

      if (!active.moved) {
        if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
        active.moved = true;
        setDragging(true);
      }

      event.preventDefault();
      const next = clampPosition(active.originX + dx, active.originY + dy);
      setPosition(next);
    },
    onPointerUp: finishDrag,
    onPointerCancel: finishDrag,
    onLostPointerCapture: (event) => {
      if (dragRef.current?.pointerId === event.pointerId) {
        finishDrag(event);
      }
    },
    onClickCapture: (event) => {
      if (!suppressClickRef.current) return;
      suppressClickRef.current = false;
      event.preventDefault();
      event.stopPropagation();
    },
  };

  const floatingStyle = position
    ? {
        left: `${position.x}px`,
        top: `${position.y}px`,
        right: "auto",
        bottom: "auto",
      }
    : {};

  return {
    elementRef,
    pointerHandlers,
    floatingStyle: {
      ...floatingStyle,
      touchAction: "none",
      userSelect: "none",
      WebkitUserSelect: "none",
      cursor: dragging ? "grabbing" : "grab",
      willChange: "left, top",
    },
    dragging,
  };
}
